import {
  CreateFormDocument,
  FormsElementTypeEnum,
  UpdateFormDocument,
} from 'generated/resources/graphql'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useMutation } from '../../core/hooks/useMutation'
import app from '../../core/store/models/app'
import { Element } from '../lib/Element'
import { Form } from '../lib/Form'
import { LongTextElement } from '../lib/elements/LongTextElement'
import { MultipleChoiceElement } from '../lib/elements/MultipleChoiceElement'
import { NumberElement } from '../lib/elements/NumberElement'
import { RatingElement } from '../lib/elements/RatingElement'
import { SectionElement } from '../lib/elements/SectionElement'
import { SingleChoiceElement } from '../lib/elements/SingleChoiceElement'
import { TextElement } from '../lib/elements/TextElement'
import { ElementOption } from '../lib/types'

type ElementConstructor = new (
  id: string,
  label: string,
  description: string,
  position: number,
  mandatory: boolean,
  params: Record<string, unknown>,
  options: ElementOption[],
  sectionId: string | null
) => Element

// This definition is used to know the class depending
// on the element type
const elementTypeToClass: { [key in FormsElementTypeEnum]: ElementConstructor } = {
  text: TextElement,
  longtext: LongTextElement,
  select: SingleChoiceElement,
  multiselect: MultipleChoiceElement,
  number: NumberElement,
  section: SectionElement,
  rating: RatingElement,
}

// The object shape for 'FormsElement'
type ElementInput = {
  id?: string
  description: string
  label: string
  mandatory: boolean
  options: ElementOption[]
  params: string
  position: number
  sectionId?: string
  type: FormsElementTypeEnum
}

// The object shape for 'FormsForm'
type FormInput = {
  id: string
  anonymous: boolean
  description?: string
  elements: ElementInput[]
  resourceId: string
  resourceType: string
  status: string
  title: string
}

export const formFromData = (formData: FormInput) => {
  const form = new Form(
    formData.id,
    formData.title,
    formData.description ?? '',
    formData.resourceType,
    formData.resourceId
  )

  formData.elements.sort((a: ElementInput, b: ElementInput) => {
    // order by section asc
    if (a.sectionId !== b.sectionId) {
      return Number(a.sectionId) - Number(b.sectionId)
    }
    //if same section, order by position
    if (a.position !== b.position) {
      return a.position - b.position
    }
    //if same position, order by id
    return Number(a.id) - Number(b.id)
  })

  let section: SectionElement | undefined
  formData.elements.forEach((elementData) => {
    if (elementData.type in elementTypeToClass) {
      const newElement = elementFromData(elementData)

      if (newElement.sectionId) {
        if (!section || section.id != newElement.sectionId) {
          section = form.elements
            .filter((element) => element instanceof SectionElement)
            .find((element) => element.id == newElement.sectionId)
        }
        if (section) {
          section.addElement(newElement)
        } else {
          throw new Error(`section: ${elementData.sectionId} not found`)
        }
      } else {
        form.addElement(newElement)
      }
    } else {
      throw new Error(`Unknown element type: ${elementData.type}`)
    }
  })
  return form
}

/**
 * Creates a form element with the data coming from backend or other place
 *
 * @param elementData
 */
export const elementFromData = (elementData: ElementInput): Element => {
  const elementClass = elementTypeToClass[elementData.type]
  if (!elementClass) {
    throw new Error(`Unknown element type: ${elementData.type}`)
  }
  const rawParams = JSON.parse(elementData.params)
  function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  const safeParams = isRecord(rawParams) ? rawParams : {}

  const newElement = new elementClass(
    elementData.id ?? '',
    elementData.label,
    elementData.description ?? '',
    elementData.position,
    elementData.mandatory,
    safeParams,
    [],
    elementData.sectionId ?? null
  )
  if (elementData.options.length > 0) {
    elementData.options.forEach((option) => {
      newElement.addOption({ value: option.value, label: option.label, id: option.id })
    })
  }
  return newElement
}

export const getDefaultOptions = (type: FormsElementTypeEnum): ElementOption[] => {
  const scale: ElementOption[] = [
    { label: i18n.t('forms.default_rating_scale.very_low'), value: '1', id: '' },
    { label: i18n.t('forms.default_rating_scale.low'), value: '2', id: '' },
    { label: i18n.t('forms.default_rating_scale.average'), value: '3', id: '' },
    { label: i18n.t('forms.default_rating_scale.high'), value: '4', id: '' },
    { label: i18n.t('forms.default_rating_scale.very_high'), value: '5', id: '' },
  ]
  const defaultOptions = (type: FormsElementTypeEnum): ElementOption[] => {
    switch (type) {
      case 'multiselect':
      case 'select':
        return [{ label: i18n.t('forms.editor.options.default_label'), value: '', id: '' }]
      case 'rating':
        return scale
      default:
        return []
    }
  }

  return defaultOptions(type)
}

export const useCreateForm = () => {
  const [createForm, { loading }] = useMutation(CreateFormDocument, {
    extractorFn: (data) => data.formsMutations.createFormsForm,
  })

  return {
    creatingForm: loading,
    createForm: async (form: Form, companyId: number, accessId: number) => {
      const result = await createForm({
        variables: {
          anonymous: true, //TODO Remove?
          authorId: accessId,
          companyId: companyId, //TODO Remove?
          description: form.description || '',
          elements: form.elements.map((e) => e.toBackend()),
          formType: 'custom', //TODO Remove?
          resourceId: form.resourceId,
          resourceType: form.resourceType,
          title: form.title,
        },
      })
      return handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: 'There was an error creating the form. Please try again', //TODO translate
        })
      })
    },
  }
}

export const useUpdateForm = () => {
  const [updateForm, { loading }] = useMutation(UpdateFormDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsForm,
  })

  return {
    updatingForm: loading,
    updateForm: async (form: Form) => {
      const result = await updateForm({
        variables: {
          id: form.id,
          anonymous: true, //TODO Remove?
          description: form.description || '',
          elements: form.elements.map((e) => e.toBackend()),
          title: form.title,
        },
      })
      return handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: 'There was an error updating the form. Please try again', //TODO translate
        })
      })
    },
  }
}
